/**
 * Keyboard accessibility helpers.
 *
 * An audit found 242 `<div onClick=…>` across the app with no keyboard route to
 * them. They are not all the same problem, and treating them as one would make
 * things worse:
 *
 *   • ~92 are genuine controls — stat cards, rows, tiles. A mouse user can click
 *     them; a keyboard or screen-reader user cannot reach them at all. These need
 *     `clickable()`.
 *   • ~87 are modal/sidebar backdrops. The correct fix is Escape-to-close, NOT
 *     making the backdrop focusable — a focusable scrim is its own anti-pattern,
 *     and screen readers announce a button that does nothing meaningful.
 *   • ~63 only carry `stopPropagation` as event plumbing. They are not interactive
 *     and must stay out of the tab order entirely.
 *
 * This matters commercially as well as legally: CBRE, JLL and the pension-fund
 * managers all carry accessibility requirements in vendor procurement.
 */
import { useEffect } from 'react';

/**
 * Spread onto a non-button element that behaves like a button.
 *
 *   <div className="stat-card" {...clickable(() => setSection('jobs'))}>
 *
 * Enter and Space both activate, matching a real <button>. Space is
 * preventDefault-ed so the page doesn't scroll underneath.
 *
 * Prefer an actual <button> where the styling allows it — this is for the cases
 * where converting the element would mean unpicking a lot of CSS.
 *
 * @param {(e: Event) => void} onActivate
 * @param {{label?: string, disabled?: boolean}} [opts] label sets aria-label when
 *        the element's own text doesn't describe the action.
 */
export const clickable = (onActivate, opts = {}) => {
  const { label, disabled } = opts;
  if (disabled) return { 'aria-disabled': true };
  const fire = (e) => { if (typeof onActivate === 'function') onActivate(e); };
  return {
    role: 'button',
    tabIndex: 0,
    onClick: fire,
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        fire(e);
      }
    },
    ...(label ? { 'aria-label': label } : {}),
  };
};

/**
 * Close on Escape — the keyboard equivalent of clicking a modal backdrop.
 *
 * Use alongside the existing click-to-dismiss handler; the backdrop itself stays
 * unfocusable, which is what assistive technology expects.
 *
 * @param {() => void} onClose
 * @param {boolean} [active=true] usually the modal's own open flag
 */
export function useEscapeClose(onClose, active = true) {
  useEffect(() => {
    if (!active || typeof onClose !== 'function') return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(e); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, active]);
}

/**
 * Marks a div that exists only to stop a click bubbling. Documents the intent so
 * the next audit doesn't "fix" it into the tab order.
 */
export const stopsPropagation = { onClick: (e) => e.stopPropagation() };
